import { sha256DigestSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/dp-image-digests.ts
/**
* The digest map of one DP version (2026-10-16-digest-pin-and-grants-by-subject, P-2 to P-5).
*
* `bin/release-dp` reads the five image digests of the signed DP build record and sends them as
* `imageDigests` in `POST /v1/releases/versions`. release-engine stores the map in
* `release.app_versions.image_digests` and returns it on `GET /v1/internal/releases/dp/latest` and
* `GET /v1/internal/releases/dp/versions/:version`. provisioning-svc writes it to the DataPlane XR as
* `spec.images.digests`; the composition copies it to the DP chart value `global.imageDigests`, and
* the chart helper `zion-dp.image` appends `@<digest>` to each image the map names.
*
* Producer: release-engine (the register door and both internal reads). Consumer: provisioning-svc.
*/
/**
* The five DP image names of a DP build record, in build order: `DP_SERVICES` in
* `bin/lib/dp-images.sh`. `cloud/e2e/parity/__tests__/registry-consistency.spec.ts` holds the two
* lists equal. The DP chart helper looks each image up by its `image.name`, which is the same name.
*/
const DP_IMAGE_DIGEST_NAMES = [
	"core-api",
	"shell",
	"zion-operator",
	"asset-proxy",
	"kernel-shell"
];
/**
* The digest map: image name to the `sha256:<64 hex>` digest of the signed DP build record.
*
* Rules:
* - The keys come from {@link DP_IMAGE_DIGEST_NAMES}; any other key refuses.
* - Each key is optional, and at least one key holds a digest. A key whose value is `undefined`
*   counts as absent, so `{ shell: undefined }` refuses.
* - Each value is the shared `sha256DigestSchema` of `@zion/sdk-contracts`.
*/
const dpImageDigestsSchema = z.partialRecord(z.enum(DP_IMAGE_DIGEST_NAMES), sha256DigestSchema).refine((map) => Object.values(map).some((digest) => digest !== void 0), { message: "imageDigests holds at least one image digest" });
/**
* The answer of both internal DP version reads: the `dp/latest` read and the by-version read.
* Response-only, so not `.strict()`.
*
* - `version`: the semver of the `release.app_versions` row (`fixture_name = 'platform'`).
* - `imageTag`: the tag `bin/release-dp` registered for the version.
* - `registeredAt`: the ISO-8601 `created_at` of the row.
* - `imageDigests`: the digest map, or `null` for a row registered with none. Optional on the wire,
*   so a provisioning-svc pod that reads an older release-engine during a roll takes an absent key
*   as `null`.
*/
const dpPlatformVersionSchema = z.object({
	version: z.string(),
	imageTag: z.string(),
	registeredAt: z.string(),
	imageDigests: dpImageDigestsSchema.nullable().optional()
});
/** The longest DP version, in characters: the Docker tag limit. */
const DP_VERSION_MAX_LENGTH = 128;
/**
* The semver grammar alone, with no length cap. `dpVersionParamsSchema` binds it beside
* `.max(DP_VERSION_MAX_LENGTH)`, so the OpenAPI document keeps one `pattern` and one `maxLength`.
*/
const DP_VERSION_GRAMMAR = /^(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)\.(?:0|[1-9]\d*)(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;
/**
* The version grammar of the by-version read: `MAJOR.MINOR.PATCH`, an optional pre-release and an
* optional build metadata part, at most 128 characters. Every version that
* `LatestVersionResolver.findLatest` keeps passes it, because `findLatest` filters on this grammar
* and on `semver.valid`. The two filters differ: `semver.valid` accepts `v1.2.3` and ` 1.2.3`, which
* this pattern refuses, and this pattern admits `2.0.0-01`, which `semver.valid` refuses. It also
* admits each version that the upgrade routes of provisioning-svc accept. A string passes this pattern exactly when it passes
* {@link dpVersionParamsSchema}, so a caller that guards with the pattern alone never reaches the
* `TypeError` of {@link dpVersionInternalPath}.
*/
const DP_VERSION_PATTERN = new RegExp(`(?=^.{0,${DP_VERSION_MAX_LENGTH}}$)${DP_VERSION_GRAMMAR.source}`);
/** The path parameter of the by-version read. `.strict()`. */
const dpVersionParamsSchema = z.object({ version: z.string().max(DP_VERSION_MAX_LENGTH).regex(DP_VERSION_GRAMMAR) }).strict();
/** The latest-version read of release-engine's internal controller. */
const DP_LATEST_VERSION_INTERNAL_PATH = "/v1/internal/releases/dp/latest";
/**
* The by-version read: `DP_VERSION_INTERNAL_PATH + '/' + encodeURIComponent(version)`. release-engine
* answers {@link dpPlatformVersionSchema} for the named version, and 404 when no visible `platform`
* row holds it.
*/
const DP_VERSION_INTERNAL_PATH = "/v1/internal/releases/dp/versions";
/**
* The path of the by-version read for one version. Throws `TypeError` when the version fails
* {@link dpVersionParamsSchema}, so no caller byte such as `/` or `..` reaches the path.
*/
function dpVersionInternalPath(version) {
	if (!dpVersionParamsSchema.safeParse({ version }).success) throw new TypeError(`dpVersionInternalPath: ${JSON.stringify(version)} is not a DP version`);
	return `${DP_VERSION_INTERNAL_PATH}/${encodeURIComponent(version)}`;
}
//#endregion
export { DP_IMAGE_DIGEST_NAMES, DP_LATEST_VERSION_INTERNAL_PATH, DP_VERSION_INTERNAL_PATH, DP_VERSION_PATTERN, dpImageDigestsSchema, dpPlatformVersionSchema, dpVersionInternalPath, dpVersionParamsSchema };

//# sourceMappingURL=dp-image-digests.js.map